import { useCallback, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { DateTime } from 'luxon';

import { useOnlineStatus } from '../components/Hooks/useOnlineStatus';
import useShiftTimes from '../components/Hooks/useShiftTimes';
import { useSignalRContext } from '../components/Contexts/SignalRContext';
import { ErrorResponse } from '../DTOs/Common/ErrorResponse';
import { geologyAjax } from '../ajax/GeologyAjax';
import { operationsAjax } from '../ajax/OperationsAjax';
import { stoppagesAjax } from '../ajax/StoppagesAjax';
import { OptionResponse } from '../DTOs/Common/OptionResponse';
import { plantAjax } from '../ajax/PlantAjax';
import { diggingAjax } from '../ajax/DiggingAjax';
import { referencesAjax } from '../ajax/ReferencesAjax';
import { productionAjax } from '../ajax/ProductionAjax';
import { PlantDTO } from '../DTOs/Plants/PlantDTO';
import { DiggingLocationDTO } from '../DTOs/Production/DiggingLocationDTO';
import { StoppageCalc } from '../DTOs/Stoppage/StoppageCalc';
import { StoppageDto } from '../DTOs/Stoppage/StoppageDto';
import { PagedResult } from '../DTOs/Common/PagedResult';

const STALE_MS = 5 * 60 * 1000;

function extractItems(res: any): any[] {
  if (Array.isArray(res)) return res;
  if (res && Array.isArray(res.data)) return res.data;
  return [];
}

export function usePrefetchCriticalPages() {
  const queryClient = useQueryClient();
  const isOnline = useOnlineStatus();
  const { selectedDate, selectedShift } = useShiftTimes();
  const { connection } = useSignalRContext();

  const runPrefetches = useCallback((staleTime: number) => {
    if (!selectedDate?.isValid) return;

    const date = selectedDate;
    const shift = selectedShift;
    const dateStr = date.toISODate()!;

    const pq = (key: unknown[], fn: () => Promise<unknown>) =>
      queryClient.prefetchQuery({ queryKey: key, queryFn: fn, staleTime });

    const pqOptions = (key: unknown[], fn: () => Promise<any>) =>
      pq(key, async () => {
        const res = await fn();
        return ErrorResponse.isErrorResponse(res) ? [] : extractItems(res);
      });

    // Note: every plant with its department. The Stoppages list colours its Machine cell from the
    // department, and a stoppage row queued offline carries only the machine's name - so without this
    // list cached there is nothing offline that knows a machine's department, and the cell rendered
    // uncoloured. Not date-scoped: a plant's department does not change with the shift.
    pqOptions(['references', 'plantsWithDepartments'], () => referencesAjax.getPlants({ pageNumber: 1, pageSize: 1000 }));

    // Note: the job types the Multiple Stoppages modal filters machines by. It was only ever fetched
    // when that modal opened, so a browser that had never opened it while online had nothing cached
    // and the filter rendered empty - which is how it looked on a fresh profile. Key must match
    // PLANT_JOB_TYPES_KEY.
    pqOptions(['references', 'plantJobTypes'], () => plantAjax.getPlantJobTypes());

    pqOptions(['geology', 'rehandleDiggers', dateStr, shift], () => geologyAjax.getRehandleDiggers(date, shift));
    pqOptions(['geology', 'rehandleTrucks', dateStr], () => geologyAjax.getRehandleTruckingTrucks(date));
    pqOptions(['geology', 'rehandleFromLocations', dateStr], () => geologyAjax.getRehandleMovementFromLocations(date));
    pqOptions(['geology', 'rehandleToLocations', dateStr], () => geologyAjax.getRehandleMovementToLocations(date));

    pq(
      ['dataTable', 'geology', 'rehandles', dateStr, shift, 'all', false, null],
      async () => {
        const res = await geologyAjax.getGeoRehandles(date, shift, { pageNumber: 1, pageSize: 100000 });
        if (ErrorResponse.isErrorResponse(res)) throw new Error(res.messages.join(', '));
        return res;
      }
    );

    pqOptions(['geology', 'crushingFromLocations', dateStr], () => geologyAjax.getCrushingMovementFromLocations(date));
    pqOptions(['geology', 'crushingToLocations', dateStr], () => geologyAjax.getCrushingMovementToLocations(date));
    pqOptions(['geology', 'crushingLoaders', dateStr, shift], () => geologyAjax.getCrushingLoaders(date, shift));

    pq(
      ['dataTable', 'geology', 'crushing', dateStr, shift, 'all', false, null],
      async () => {
        const res = await geologyAjax.getGeoCrushingEntries(date, shift, { pageNumber: 1, pageSize: 100000 });
        if (ErrorResponse.isErrorResponse(res)) throw new Error(res.messages.join(', '));
        return res;
      }
    );

    pq(['references', 'stoppageTypes'], async () => {
      const res = await stoppagesAjax.getStoppageTypes();
      return ErrorResponse.isErrorResponse(res) ? new OptionResponse([]) : res;
    });
    // Note: every stoppage sub type in one request; the Breakdown Code dropdown reads this cache
    // and filters client-side, so it works offline (see BreakdownCodeCell).
    pq(['references', 'stoppageSubTypes'], async () => {
      const res = await stoppagesAjax.getStoppageSubtypes({ pageNumber: 0, pageSize: 1000 } as any);
      return ErrorResponse.isErrorResponse(res) ? [] : res.data;
    });
    pqOptions(['references', 'jobTypes'], () => referencesAjax.getJobTypes());
    // Note: the Create Multiple Stoppages modal's job type filter reads this; warmed here so the
    // modal still works offline.
    pq(['references', 'plantJobTypes'], async () => {
      const res = await plantAjax.getPlantJobTypes();
      return ErrorResponse.isErrorResponse(res) ? [] : res.data;
    });
    pqOptions(['references', 'locations'], () => referencesAjax.getLocations({} as any));
    pqOptions(['references', 'departments'], () => referencesAjax.getDepartments({ pageNumber: 1, pageSize: 100 } as any));

    // Note: the stoppages list uses clientPaginate; warm its single page-agnostic cache entry (for
    // the default filters) with the whole shift so every page is available offline in-memory.
    pq(
      ['dataTable', 'stoppages', 'list', dateStr, shift, 'Down & Up', null, '', null, 'all', false, null],
      async () => {
        const res = await stoppagesAjax.getStoppagesMaintenance({
          pageNumber: 1, pageSize: 100000,
          shiftDate: dateStr, dayOrNight: shift[0],
          status: '', machineName: '',
        } as any);
        if (ErrorResponse.isErrorResponse(res)) throw new Error(res.messages.join(', '));
        return res;
      }
    );

    const defaultOpFilters = [{ key: 'Status', value: '' }, { key: 'MachineName', value: '' }];
    const opKey = ['dataTable', 'operations', 'stoppageCalcs', dateStr, shift, defaultOpFilters, 1, 5, null, 'asc', false, null];
    // Note: swallow the failure like the digger block below. getStoppagesForShift rejects with the
    // bare string "CANCELLED" when its in-flight request is superseded (the effect re-runs on a
    // date/shift change), which surfaced as an unhandled promise rejection. The consequence of a
    // cancelled run is only that these caches stay cold until the next prefetch warms them.
    (async () => {
      try {
        await queryClient.prefetchQuery({
          queryKey: opKey,
          queryFn: async () => {
            const res = await operationsAjax.getStoppageCalcsData(date, shift, { Status: '', MachineName: '', pageNumber: 0, pageSize: 10 });
            if (ErrorResponse.isErrorResponse(res)) throw new Error(res.messages.join(', '));
            return res;
          },
          staleTime,
        });
        const calcs = queryClient.getQueryData<PagedResult<StoppageCalc>>(opKey);

        // Note: one shift-wide request warms every per-plant plantStoppages cache, instead of one
        // request per plant.
        const plantStoppageGroups = await stoppagesAjax.getStoppagesForShift(date, shift);
        const stoppagesByPlant = new Map<number, PagedResult<StoppageDto>>();
        if (!ErrorResponse.isErrorResponse(plantStoppageGroups)) {
          for (const group of plantStoppageGroups) stoppagesByPlant.set(group.plantId, group.stoppages);
        }
        for (const entry of calcs?.data ?? []) {
          queryClient.setQueryData(
            ['dataTable', 'operations', 'plantStoppages', dateStr, shift, entry.plantId, entry.status, 1, 5, null, 'asc', false, null],
            stoppagesByPlant.get(entry.plantId) ?? PagedResult.empty<StoppageDto>(1, 5)
          );
        }
      } catch {
        // ignore - cancelled, offline or server error
      }
    })();

    // Note: the summary list uses clientPaginate, so warm its single page-agnostic cache entry
    // with the whole shift. Every page is then served in-memory offline from this one blob.
    pq(
      ['dataTable', 'chatree', 'diggingLoadingSummary', dateStr, shift, 'all', false, null],
      async () => {
        const res = await diggingAjax.getDiggingLoadingSummary(date, shift, { pageNumber: 1, pageSize: 100000 });
        if (ErrorResponse.isErrorResponse(res)) throw new Error(res.messages.join(', '));
        return res;
      }
    );

    pqOptions(['production', 'locationOptions', dateStr, shift], () => diggingAjax.getLocationOptions(date, shift));
    pqOptions(['geology', 'productionTrucks', dateStr], () => geologyAjax.getProductionTruckingTrucks(date));

    // Note: these three warm independently. They used to run in one chain, so a cancelled or failed
    // first request (the digger list is superseded whenever the date or shift changes) skipped the
    // two after it and nothing retried - which is how a shift could go offline with no digger
    // locations cached, leaving an offline shift completion nothing to carry over.
    (async () => {
      try {
        await queryClient.fetchQuery<PlantDTO[]>({
          queryKey: ['chatree', 'plants', dateStr, shift],
          queryFn: async () => {
            const res = await plantAjax.getAvailableDiggers(date, shift);
            return (ErrorResponse.isErrorResponse(res) ? [] : extractItems(res)) as PlantDTO[];
          },
          staleTime,
        });
      } catch {
        // ignore - cancelled, offline or server error
      }
    })();

    (async () => {
      try {
        // Note: one shift-wide request warms every per-digger locations cache, instead of firing
        // one request per digger (the main per-item fan-out on load).
        const locationGroups = await diggingAjax.getDiggerLocationsForShift(date, shift);
        if (!ErrorResponse.isErrorResponse(locationGroups)) {
          for (const group of locationGroups) {
            queryClient.setQueryData(
              ['dataTable', 'chatree', 'diggerLocations', dateStr, shift, group.diggerId, 1, 5, null, 'asc', false, null],
              group.result
            );
          }
        }
      } catch {
        // ignore - cancelled, offline or server error
      }
    })();

    (async () => {
      try {
        // Note: one shift-wide request warms every per-pattern-per-hour truck-loads cache, instead
        // of firing pattern-count x 12 individual requests.
        const shiftLoadGroups = await diggingAjax.getTruckLoadsForShift(date, shift, { pageNumber: 1, pageSize: 5 });
        if (!ErrorResponse.isErrorResponse(shiftLoadGroups)) {
          for (const group of shiftLoadGroups) {
            queryClient.setQueryData(
              ['dataTable', 'chatree', 'truckLoads', group.diggingPatternId, group.hour, 1, 5, null, 'asc', false, null],
              group.result
            );
          }
        }
      } catch {
        // ignore - offline or server error
      }
    })();

    pq(['production', 'allowedNonWasteLocationTypeIds'], async () => {
      const res = await productionAjax.getAllowedNonWasteLocationTypeIds();
      return ErrorResponse.isErrorResponse(res) ? [] : res;
    });
    pq(['production', 'allowedWasteLocationTypeIds'], async () => {
      const res = await productionAjax.getAllowedWasteLocationTypeIds();
      return ErrorResponse.isErrorResponse(res) ? [] : res;
    });
    pqOptions(['references', 'blastmasterIds', dateStr, shift], () => diggingAjax.getBlastmasterIds(date, shift));
    pq(['references', 'pitStages'], async () => {
      const res = await referencesAjax.getPitStages({ pageNumber: 1, pageSize: 100 } as any);
      if (ErrorResponse.isErrorResponse(res)) return [];
      return res.data;
    });
    pq(['references', 'materialTypes', { nonWasteOnly: false, isLive: true }], async () => {
      const res = await referencesAjax.getMaterialTypes({ nonWasteOnly: false, isLive: true, pageNumber: 1, pageSize: 100 } as any);
      if (ErrorResponse.isErrorResponse(res)) return [];
      return res.data;
    });
    pq(['references', 'materialTypeClasses', { isLive: true }], async () => {
      const res = await referencesAjax.getMaterialTypeClasses({ isLive: true, pageNumber: 1, pageSize: 100 } as any);
      if (ErrorResponse.isErrorResponse(res)) return [];
      return res.data;
    });
  }, [queryClient, selectedDate, selectedShift]);

  useEffect(() => {
    if (!isOnline || !selectedDate?.isValid) return;
    // Note: warm the offline cache immediately. The batched endpoints keep this light, and a
    // deferred (requestIdleCallback) warm-up proved unreliable because the live dashboard keeps
    // the tab busy, so the cache was not ready if the user went offline soon after load.
    runPrefetches(STALE_MS);
  }, [isOnline, selectedDate, selectedShift, runPrefetches]);

  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!connection) return;

    const refresh = () => {
      if (!selectedDate?.hasSame(DateTime.now(), 'day')) return;
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
      // Note: on a live data change, invalidate the cached data tables rather than eagerly
      // re-running the whole prefetch. Invalidation refetches only the queries that are currently
      // mounted (the page the user is viewing); everything else is marked stale and refreshes
      // lazily the next time it is opened. This avoids re-pulling the entire shift - every list
      // plus the client-paginate fetch-alls - on every single update. The one-time prefetch above
      // still warms the full offline cache on load.
      refreshTimerRef.current = setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ['dataTable'] });
      }, 2000);
    };

    connection.on('DataUpdatedAsync', refresh);
    connection.on('DataInsertedAsync', refresh);
    connection.on('DataDeletedAsync', refresh);

    return () => {
      connection.off('DataUpdatedAsync', refresh);
      connection.off('DataInsertedAsync', refresh);
      connection.off('DataDeletedAsync', refresh);
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    };
  }, [connection, selectedDate, queryClient]);
}
